import { useCallback, useEffect, useState, useRef } from "react";
import { useGetExternalPerformanceMutation } from "../../store/api/devices/deviceApiSlice";
import { toTimespanRangeMs } from "../../store/helpers/helpers";
import { useStoreDispatch } from "../../store/hooks";
import { selectedDeviceSelector } from "../../store/api/devices/selectors";
import { useSelector } from "react-redux";
import { IExternalPerformanceArgs } from "../../store/api/deviceConfig/types";
import { setOpenToast } from "../../store/helpers/helperReducers";
import { errorMessage } from "../../utils/helpers";
import { searchKeywordSelector } from "../../store/search/selector";
import { selectedGeoRegionsSelector, startDateSelector } from "../../store/api/admin/selectors";
import { setStartDate } from "../../store/api/admin/reducers";
import { resetKeyword } from "../../store/search/reducer";
import { IGeoRegion } from "../../store/api/admin/types";

export const usePerformanceDisplay = () => {
  const [getExternalPerformance] = useGetExternalPerformanceMutation();
  const dispatch = useStoreDispatch();
  const currentRequestId = useRef<number>(0);

  const healthFilterDefault = "all";
  const [healthFilter, setHealthFilter] = useState(healthFilterDefault);
  const [selectedTab, setSelectedTab] = useState<string>("LTE");

  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [initialData, setInitialData] = useState([]);

  const selectedDevice: IExternalPerformanceArgs = useSelector(selectedDeviceSelector);
  const startDate = useSelector(startDateSelector);
  const selectedGeoRegions = useSelector(selectedGeoRegionsSelector) as IGeoRegion[];
  const search = useSelector(searchKeywordSelector);

  useEffect(() => {
    if (search?.length > 0 && initialData?.length > 0) {
      const newArr = initialData.filter((item: any) =>
        item?.geoRegion?.toLowerCase()?.includes(search.toLowerCase())
      );
      setData([...newArr]);
    } else {
      setData([...initialData]);
    }
  }, [initialData, search]);

  const fetchExternalPerformanceData = useCallback(async () => {
    const rawTimeSpan = toTimespanRangeMs(startDate);
    const timeSpan = {
      from: Math.floor(Number(rawTimeSpan.from) / 1000),
      to: Math.floor(Number(rawTimeSpan.to) / 1000),
    };

    const regionNames = (selectedGeoRegions || [])
      .map((region) => region?.name?.trim())
      .filter((name): name is string => !!name);

    // Build repeated region= params so Spring Boot binds them as List<String>.
    // DO NOT join with commas and encodeURIComponent — that encodes the comma
    // separator and the backend receives a single literal string instead of a list.
    const regionParts = regionNames.map((r) => `region=${encodeURIComponent(r)}`);

    const parts: string[] = [
      `to=${timeSpan.to}`,
      `from=${timeSpan.from}`,
      `health=${healthFilter}`,
      ...regionParts,
    ];

    const args = {
      filters: parts.join("&"),
      selectedTab: selectedTab,
    };

    // Increment currentRequestId to track specific request
    const requestId = ++currentRequestId.current;
    setIsLoading(true);

    try {
      const response: any = await getExternalPerformance(args);
      // Check if response is from current request
      if (requestId !== currentRequestId.current) {
        // if response from outdated request, ignore it
        return;
      }

      if (response.error || !response.data) {
        throw response.error?.data || "Unknown error";
      }

      if (Array.isArray(response.data)) {
        setData(response.data);
        setInitialData(response.data);
        setIsLoading(false);
      }
    } catch (error: any) {
      // Only update loading state if still current request
      if (requestId === currentRequestId.current) {
        // Clear data to avoid showing stale LTE rows when NR fetch fails (and vice versa)
        setData([]);
        setInitialData([]);
        setIsLoading(false);
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "error",
            message: errorMessage(error),
          })
        );
      }
    }
  }, [dispatch, getExternalPerformance, healthFilter, selectedGeoRegions, startDate, selectedTab]);

  const handleResetFilter = () => {
    dispatch(resetKeyword());
    dispatch(setStartDate(24 * 60 * 60 * 1000));
    setHealthFilter(healthFilterDefault);
  };

  useEffect(() => {
    fetchExternalPerformanceData();
  }, [fetchExternalPerformanceData]);

  useEffect(() => {
    setData([]);
    setInitialData([]);
  }, [selectedTab]);

  const [selectedRow, setSelectedRow] = useState(null);
  const [rowClicked, setRowClicked] = useState(false);

  return {
    selectedRow,
    setSelectedRow,
    rowClicked,
    setRowClicked,
    data,
    isLoading,
    healthFilter,
    setHealthFilter,
    selectedDevice,
    setIsLoading,
    selectedTab,
    setSelectedTab,
    handleResetFilter
  };
};
